import { z } from "zod";
import { actionAvailabilitySchema, instantSchema, reasonSchema, type Stage } from "@amber/contracts";
import type { CostService } from "./records.js";

export interface CredentialDefinition {
  readonly name: string;
  readonly provider: string;
  readonly service: CostService;
}

/** The provider credentials named in `.env.example`; only names are kept, never values (QO-26). */
export const CREDENTIALS: readonly CredentialDefinition[] = [
  { name: "YOUTUBE_API_KEY", provider: "Google (YouTube Data API)", service: "youtube_data_api" },
  { name: "WEBSHARE_API_KEY", provider: "Webshare", service: "webshare" },
  { name: "TWITTERAPI_IO_API_KEY", provider: "twitterapi.io", service: "twitterapi_io" },
  { name: "NEWSAPI_AI_API_KEY", provider: "NewsAPI.ai", service: "newsapi_ai" },
  { name: "ASSEMBLYAI_API_KEY", provider: "AssemblyAI", service: "assemblyai" },
  { name: "PYANNOTEAI_API_KEY", provider: "pyannoteAI", service: "pyannoteai" },
  { name: "OPENROUTER_API_KEY", provider: "OpenRouter", service: "openrouter" },
  { name: "ELEVENLABS_API_KEY", provider: "ElevenLabs", service: "elevenlabs" },
  { name: "OPENAI_API_KEY", provider: "OpenAI", service: "openai" },
  { name: "GEMINI_API_KEY", provider: "Gemini", service: "gemini" },
];

export interface LicenceDefinition {
  readonly key: string;
  readonly provider: string;
  readonly label: string;
}

/** Acquired plans and credit with a renewal date (build brief spend caps: existing plans only). */
export const LICENCES: readonly LicenceDefinition[] = [
  { key: "webshare_subuser_plan", provider: "Webshare", label: "Webshare sub-user plan" },
  { key: "newsapi_ai_plan", provider: "NewsAPI.ai", label: "NewsAPI.ai plan" },
  { key: "twitterapi_io_credit", provider: "twitterapi.io", label: "twitterapi.io credit" },
];

export interface DependencyFunction {
  readonly key: string;
  readonly label: string;
  /** The providers that can carry this function, the primary first. */
  readonly services: readonly [CostService, ...CostService[]];
  readonly stage?: Stage;
  readonly work?: "processing";
}

export const DEPENDENCY_FUNCTIONS: readonly DependencyFunction[] = [
  { key: "transcription", label: "Transcription", services: ["assemblyai", "openrouter", "elevenlabs"] },
  { key: "diarization", label: "Diarization", services: ["pyannoteai"] },
  {
    key: "speaker_naming",
    label: "Speaker naming",
    services: ["openai", "gemini"],
    stage: "speakers",
    work: "processing",
  },
  { key: "translation", label: "Translation", services: ["openai", "gemini"], stage: "ready", work: "processing" },
  { key: "x_discovery", label: "X discovery", services: ["twitterapi_io"] },
  { key: "news_discovery", label: "News discovery", services: ["newsapi_ai"] },
  { key: "proxies", label: "Proxies", services: ["webshare"] },
  { key: "youtube_metadata", label: "YouTube metadata", services: ["youtube_data_api"] },
];

export function providerOf(service: CostService): string {
  return CREDENTIALS.find((c) => c.service === service)?.provider ?? service;
}

export const DEPENDENCY_ACTIONS = { record: "operations.record_dependency_date" } as const;
export const OPERATOR_DEPENDENCIES_REASON = "Only Operators and Admins can record expiry and renewal dates";

export const DEPENDENCY_KINDS = ["credential", "licence"] as const;
export const dependencyKindSchema = z.enum(DEPENDENCY_KINDS);
export type DependencyKind = z.infer<typeof dependencyKindSchema>;

const calendarDateSchema = z.iso.date();
export const dateStateSchema = z.enum(["none", "ok", "soon", "passed"]);
export type DateState = z.infer<typeof dateStateSchema>;

export const dependencyDateRequestSchema = z.strictObject({
  kind: dependencyKindSchema,
  key: z.string().min(1),
  date: calendarDateSchema.nullable(),
  reason: reasonSchema,
  base_revision: z.number().int().nonnegative(),
});
export type DependencyDateRequest = z.infer<typeof dependencyDateRequestSchema>;

const recordedSchema = z.strictObject({ by: z.string(), at: instantSchema, reason: z.string() }).nullable();

export const credentialStateSchema = z.strictObject({
  name: z.string(),
  provider: z.string(),
  configured: z.boolean(),
  last_verified_at: instantSchema.nullable(),
  expires_on: calendarDateSchema.nullable(),
  expiry: dateStateSchema,
  revision: z.number().int().nonnegative(),
  recorded: recordedSchema,
});
export type CredentialState = z.infer<typeof credentialStateSchema>;

export const dependenciesResponseSchema = z.strictObject({
  window_days: z.number().int().nonnegative(),
  credentials: z.array(credentialStateSchema),
  licences: z.array(
    z.strictObject({
      key: z.string(),
      provider: z.string(),
      label: z.string(),
      renews_on: calendarDateSchema.nullable(),
      renewal: dateStateSchema,
      revision: z.number().int().nonnegative(),
      recorded: recordedSchema,
    }),
  ),
  concentration: z.array(
    z.strictObject({
      function: z.string(),
      label: z.string(),
      provider: z.string(),
      share: z.number().min(0).max(100).nullable(),
      basis: z.string(),
      backup: z.string().nullable(),
    }),
  ),
  actions: z.strictObject({ record: actionAvailabilitySchema }),
});
export type DependenciesResponse = z.infer<typeof dependenciesResponseSchema>;
